import { StyleProvider } from "@ant-design/cssinjs";
import { App as AntApp, ConfigProvider, type ThemeConfig } from "antd";
import type { Locale } from "antd/lib/locale";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router";
import enUS from "antd/es/locale/en_US.js";
import bgBG from "antd/es/locale/bg_BG.js";
import dayjs from "dayjs";
import "dayjs/locale/bg.js";
import { CalculabelApolloProvider } from "./apollo/CalculabelApolloProvider.tsx";
import { App } from "./App.tsx";
import { freeze } from "immer";

const antLocaleMap = freeze<Record<string, Locale>>(
  {
    bg: bgBG,
    en: enUS,
  },
  true,
);

const themeConfig = freeze<ThemeConfig>(
  {
    token: {
      colorTextBase: "#030712",
      colorBgBase: "#ffffff",
      colorPrimary: "#2b7fff",
      colorInfo: "#2b7fff",
      colorSuccess: "#00c950",
      colorWarning: "#f0b100",
      colorError: "#fb2c36",
      wireframe: true,
      fontFamily:
        "-apple-system, BlinkMacSystemFont, Segoe UI, Noto Sans, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji",

      // Borders — antd defaults (~#d9d9d9) are too faint on white
      colorBorder: "#9ca3af",
      colorBorderSecondary: "#d1d5db",

      // Secondary text — darker than antd default for better readability
      colorTextSecondary: "#374151",
      colorTextTertiary: "#6b7280",

      red: "#fb2c36",
      orange: "#ff6900",
      gold: "#fe9a00",
      yellow: "#f0b100",
      lime: "#7ccf00",
      green: "#00c950",
      cyan: "#00b8db",
      geekblue: "#00a6f4",
      blue: "#2b7fff",
      purple: "#ad46ff",
      magenta: "#e12afb",
      pink: "#f6339a",
      volcano: "#71717b",
    },
    components: {
      Table: {
        rowHoverBg: "#f0f0f0",
      },
    },
    cssVar: { prefix: "ant", key: "light" },
    hashed: false,
    zeroRuntime: true,
  },
  true,
);

export const RootApp = () => {
  const { i18n } = useTranslation();
  // Alias i18n.language before calling .split(): i18next-cli's extractor tracks `i18n`
  // as a translation symbol and would otherwise interpret `i18n.language.split("-")`
  // as a translation call, adding a phantom "-" key to the locale JSONs on every run.
  const currentLanguage = i18n.language;
  const lang = currentLanguage.split("-")[0];
  const antLocale = antLocaleMap[lang] ?? enUS;

  useEffect(() => {
    document.documentElement.lang = lang;
    dayjs.locale(lang in antLocaleMap ? lang : "en");
  }, [lang]);

  return (
    <StyleProvider layer>
      <ConfigProvider locale={antLocale} theme={themeConfig}>
        <AntApp>
          <CalculabelApolloProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </CalculabelApolloProvider>
        </AntApp>
      </ConfigProvider>
    </StyleProvider>
  );
};
